// Imports
import { PostOrPage } from '@tryghost/content-api';
import { Link } from 'react-router-dom';
import styled from 'styled-components/macro';
import LoadingImage from '../../../resources/loading-image';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useEffect, useRef } from 'react';
import convertElementPlainQuotes from '../../../../utils/convert-element-plain-quotes';


// Styled components
const Wrapper = styled(Link)`
	width: min(24.375rem, 100%);
	text-decoration: none;
`;

const FeatureImage = styled(LoadingImage)`
	width: 100%;
	aspect-ratio: 2 / 1;
	object-fit: cover;
	margin-bottom: 1.25rem;
	border-radius: 0.625rem;
	display: block;
`;

const Tags = styled.div`
	color: hsl(206, 69%, 35%);
	font-size: 0.875rem;
	font-weight: 400;
`;

const AuthorSkeletonWrapper = styled.div`
	width: 4rem;
`;

const PublishedDateSkeletonWrapper = styled.div`
	width: 5rem;
`;

const Title = styled.h3`
	color: hsl(206, 25%, 20%);
	font-size: 1.5rem;
	font-weight: 600;
	line-height: 1.2;
	margin-top: 0.375rem;
	margin-bottom: 0.625rem;
`;

const Excerpt = styled.div`
	display: -webkit-box;
	overflow-y: hidden;
	-webkit-line-clamp: 3;
	-webkit-box-orient: vertical;
	overflow-wrap: anywhere;
	
	color: hsl(206, 25%, 20%, 1);
	font-size: 1.125rem !important;
	font-weight: 400;
	line-height: 1.2 !important;
	margin-bottom: 0 !important;
	width: min(24.375rem, 100%);
`;

const Authors = styled.div`
	display: flex;
	margin-top: 0.625rem;
	margin-right: auto;
	gap: 0.375rem;
	align-items: center;
`;

const AuthorImageContainer = styled.div`
	display: flex;
	margin-left: 0.375rem;
	margin-right: -0.125rem;
	margin-top: -0.125rem;
	margin-bottom: -0.125rem;
	height: 2.25rem;
`;

const AuthorName = styled.div`
	font-size: 0.875rem !important;
	margin-bottom: 0.25rem !important;
	color: hsl(206, 25%, 20%);
	line-height: 1 !important;
`;

const PublishedDate = styled.div`
	font-size: 0.75rem !important;
	color: hsl(206, 15%, 40%);
	line-height: 1 !important;
`;

const AuthorNamePublishedContainer = styled.div`
	width: 100%;
`;

const ProfileImage = styled(LoadingImage)`
	border: 0.125rem solid hsl(0, 0%, 100%);
	border-radius: 62.4375rem;
	width: 2.25rem;
	height: 2.25rem;
	margin-left: -0.5rem;
`;

const ProfileImageSkeleton = styled(Skeleton)`
	margin-left: -0.5rem;
`;


// Define the accepted props
interface Props {
	post?: PostOrPage;
}


// Function component
const Preview: React.FC<Props> = ({ post }) => {
	// Use ref
	const postRef = useRef<HTMLAnchorElement | null>(null);
	
	
	// Insert smart quotes
	useEffect(() => {
		if (postRef.current) {
			convertElementPlainQuotes(postRef.current);
		}
	}, [post]);
	
	
	// Return JSX
	return (
		<Wrapper ref={postRef} to={post ? `/blog/${post.slug}` : '.'}>
			<FeatureImage src={post?.feature_image ?? ''} alt={post?.feature_image_alt ?? post?.title ?? ''} />
			<Tags>
				{post ? (
					post.tags?.map((tag) => tag.name).join(', ')
				) : (
					<AuthorSkeletonWrapper>
						<Skeleton />
					</AuthorSkeletonWrapper>
				)}
			</Tags>
			<Title>{post ? post.title : <Skeleton />}</Title>
			<Excerpt>
				{post ? post.excerpt?.split('\n\n')[0].replace(/\n/g, ' ').trim() : <Skeleton count={3} width='100%' />}
			</Excerpt>
			<Authors>
				<AuthorImageContainer>
					{post ? (
						post.authors?.map((author) => (
							<ProfileImage key={author.id} src={author.profile_image ?? ''} alt={author.name} />
						))
					) : (
						<ProfileImageSkeleton height='2.25rem' width='2.25rem' borderRadius='62.4375rem' />
					)}
				</AuthorImageContainer>
				<AuthorNamePublishedContainer>
					<AuthorName>
						{post ? (
							post.authors?.map((author) => author.name).join(', ')
						) : (
							<AuthorSkeletonWrapper>
								<Skeleton />
							</AuthorSkeletonWrapper>
						)}
					</AuthorName>
					<PublishedDate>
						{post?.published_at ? (
							new Date(post.published_at).toLocaleDateString(undefined, {
								month: 'long',
								day: 'numeric',
								year: 'numeric',
							})
						) : (
							<PublishedDateSkeletonWrapper>
								<Skeleton />
							</PublishedDateSkeletonWrapper>
						)}
					</PublishedDate>
				</AuthorNamePublishedContainer>
			</Authors>
		</Wrapper>
	);
};

export default Preview;
